/**
 * The admin portal (asked for 2026-09-29): every account, what it uses, and
 * what an administrator can do about it - apps/api/src/admin.ts.
 *
 *   AdminSite    the whole site at the portal's own address (admin.foldmycard.com):
 *                its own sign-in, for administrators only, its own session and a
 *                shorter idle sign-out
 *   AdminPortal  the portal itself; also shown at /admin inside the app while the
 *                portal has no address of its own
 *
 * Pages: Overview (the numbers, who is waiting, what happened lately), Accounts
 * (search and filter), one account (its facts, devices, actions, history),
 * Activity (everything administrators did) and Backups.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  api, ApiError, type AdminLogEntry, type AdminOverview, type AdminUser, type AdminUserDetail, type Approval, type LibraryPhoto, type PromoCode,
  type SiteControls,
} from '../api.ts';
import { OCCASION_GROUPS } from '../library/index.ts';
import { preparePicture } from '../local/pictures.ts';
import { authClient, useSession } from '../auth-client.ts';
import { IdleGuard, markActive, signOutAndLeave, useSite } from '../idle.tsx';
import { LegalPage, SiteFooter } from '../legal.tsx';
import { Link, navigate, safeNext, useLocation } from '../router.tsx';
import { ago, deviceName, hasString, t, type StringKey } from '../strings.ts';
import { ThemeSwitch } from '../theme.tsx';
import { AuthCard, ForgotPage, PasswordField } from './auth.tsx';

const LEGAL: Record<string, 'terms' | 'disclaimer' | 'privacy'> = { '/terms': 'terms', '/disclaimer': 'disclaimer', '/privacy': 'privacy' };
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : t('never'));
const moment = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : t('never'));
const loading = <main className="page"><p className="muted">{t('loading')}</p></main>;

/* ------------------------------------------------------ the portal's site */

/** Everything at the portal's own address. */
export function AdminSite() {
  const loc = useLocation();
  const { data: session, isPending, isRefetching } = useSession();
  const site = useSite();
  const legal = LEGAL[loc.pathname];
  const signedOut = !isPending && !isRefetching && !session;

  useEffect(() => {
    if (!legal && loc.pathname !== '/signin' && signedOut) {
      navigate(`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true });
    }
  }, [legal, signedOut, loc.pathname, loc.search]);

  if (legal) return <><LegalPage which={legal} /><SiteFooter /></>;
  if (loc.pathname === '/signin') return <><AdminSignIn /><SiteFooter /></>;
  // the reset link in the email opens the app's page; asking for one works here too
  if (loc.pathname === '/forgot') return <><ForgotPage /><SiteFooter /></>;
  if (!session) return loading;
  return (
    <>
      <IdleGuard minutes={site?.adminIdleMinutes ?? 15} since={session.session.createdAt} />
      <AdminPortal base="" />
      <SiteFooter />
    </>
  );
}

function AdminSignIn() {
  const loc = useLocation();
  const { data: session, refetch } = useSession();
  const next = safeNext(loc.searchParams.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (session) navigate(next, { replace: true }); }, [session, next]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.signIn.email({ email, password });
    if (err) {
      setBusy(false);
      setError(err.code === 'NOT_ADMIN' ? err.message || t('somethingWrong')
        : err.status === 401 ? t('wrongPassword') : err.message || t('somethingWrong'));
      return;
    }
    markActive();
    await refetch();
    navigate(next, { replace: true });
  };

  return (
    <AuthCard title={t('adminSignInTitle')} intro={t('adminSignInIntro')} brand={t('adminPortal')}>
      {loc.searchParams.get('idle') && <p className="ok">{t('idleSignedOut')}</p>}
      <form onSubmit={submit} className="stack">
        <label>{t('email')}<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
        <PasswordField id="admin-password" label={t('password')} value={password} onChange={setPassword} autoComplete="current-password"
          forgot={<Link href="/forgot" className="small">{t('forgotPassword')}</Link>} />
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary" disabled={busy}>{t('signIn')}</button>
      </form>
    </AuthCard>
  );
}

/* -------------------------------------------------------------- the portal */

const NAV: [string, StringKey][] = [['/', 'overview'], ['/accounts', 'accounts'], ['/activity', 'activity'], ['/library', 'navLibrary'], ['/backups', 'navBackups']];

export function AdminPortal({ base }: { base: string }) {
  const loc = useLocation();
  const [me, setMe] = useState<{ email: string; appUrl: string } | null>(null);
  const [denied, setDenied] = useState(false);
  const [waiting, setWaiting] = useState(0);
  const href = (p: string) => (p === '/' ? base || '/' : `${base}${p}`);
  const sub = loc.pathname.slice(base.length) || '/';

  useEffect(() => { api.adminMe().then(setMe).catch(() => setDenied(true)); }, []);
  // the number waiting, in the menu: looked at again on every page
  useEffect(() => { api.adminOverview().then((o) => setWaiting(o.accounts.waiting)).catch(() => {}); }, [loc.pathname, loc.search]);

  if (denied) {
    return (
      <AuthCard title={t('notAdminTitle')}>
        <NotAdmin />
      </AuthCard>
    );
  }
  if (!me) return loading;

  const one = /^\/accounts\/([^/]+)$/.exec(sub);
  const page = sub === '/accounts' ? <Accounts href={href} />
    : one ? <AccountDetail key={one[1]} id={decodeURIComponent(one[1])} href={href} />
    : sub === '/activity' ? <Activity href={href} />
    : sub === '/backups' ? <BackupsPage />
    : sub === '/library' ? <LibraryPage />
    : sub === '/promo' ? <PromoPage appUrl={me.appUrl} />
    : <Overview href={href} />;
  const current = (p: string) => (p === '/' ? sub === '/' : sub === p || sub.startsWith(`${p}/`));

  return (
    <div className="admin">
      <header className="admin-bar">
        <Link href={href('/')} className="brand admin-brand">{t('appName')} <span className="admin-tag">{t('admin')}</span></Link>
        <nav className="admin-nav" aria-label={t('admin')}>
          {NAV.map(([p, label]) => (
            <Link key={p} href={href(p)} aria-current={current(p) ? 'page' : undefined}>
              {t(label)}{p === '/accounts' && waiting > 0 && <span className="count" title={t('statWaiting')}>{waiting}</span>}
            </Link>
          ))}
        </nav>
        <div className="admin-me">
          <span className="muted small admin-email">{me.email}</span>
          <a href={base ? '/' : me.appUrl} target={base ? undefined : '_blank'} rel="noopener">{t('openApp')}{base ? '' : ' ↗'}</a>
          <ThemeSwitch compact />
          <button onClick={() => signOutAndLeave()}>{t('signOut')}</button>
        </div>
      </header>
      <main className="page admin-page">{page}</main>
    </div>
  );
}

function NotAdmin() {
  const { data: session } = useSession();
  const site = useSite();
  return (
    <>
      <p>{t('notAdminBody', { email: session?.user.email ?? '' })}</p>
      <div className="row">
        {site && <a className="button primary" href={site.appUrl}>{t('openApp')}</a>}
        <button onClick={() => signOutAndLeave()}>{t('signOut')}</button>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- bits */

type Href = (p: string) => string;

const STATUS_KEY: Record<Approval, StringKey> = {
  pending: 'approvalPending', approved: 'statActive', declined: 'statDeclined', suspended: 'statusSuspended',
};
function StatusChip({ u }: { u: Pick<AdminUser, 'approval' | 'emailVerified' | 'approver'> }) {
  return (
    <span className="chips">
      {!u.emailVerified
        ? <span className="status status-unconfirmed">{t('unconfirmedBadge')}</span>
        : <span className={`status status-${u.approval}`}>{t(STATUS_KEY[u.approval])}</span>}
      {u.approver && <span className="status status-admin">{t('adminBadge')}</span>}
    </span>
  );
}

function Stat({ label, value, sub, href, tone }: { label: string; value: ReactNode; sub?: string; href?: string; tone?: 'warn' }) {
  const body = <><span className="stat-value">{value}</span><span className="stat-label">{label}</span>{sub && <span className="stat-sub">{sub}</span>}</>;
  return href ? <Link href={href} className={`stat ${tone ?? ''}`}>{body}</Link> : <div className={`stat ${tone ?? ''}`}>{body}</div>;
}

/** A change of the site controls, in words: "paused the website", "switched approval off" ... */
function settingsPhrase(detail: Record<string, unknown> | null): string {
  const d = detail ?? {};
  const parts: string[] = [];
  if (d.paused === true) parts.push(t('setPaused'));
  if (d.paused === false) parts.push(t('setResumed'));
  if (d.approvalRequired === true) parts.push(t('setApprovalOn'));
  if (d.approvalRequired === false) parts.push(t('setApprovalOff', { n: Number(d.letIn ?? 0) }));
  if (d.signUpsOpen === true) parts.push(t('setSignUpsOpen'));
  if (d.signUpsOpen === false) parts.push(t('setSignUpsClosed'));
  for (const [k, label] of [['notifySignUps', 'ctlNotifySignUps'], ['notifySignIns', 'ctlNotifySignIns']] as const) {
    if (typeof d[k] === 'boolean') parts.push(`${t(label).toLowerCase()}: ${d[k] ? t('on') : t('off')}`);
  }
  if (typeof d.pausedMessage === 'string' && d.paused === undefined) parts.push(t('setMessage'));
  return parts.join('; ') || t('setChanged');
}

/** One line of the log. Without `withTarget` (an account's own history) the account is left out. */
function LogLine({ e, href, withTarget = true }: { e: AdminLogEntry; href: Href; withTarget?: boolean }) {
  const key = `act_${e.action}`;
  const phrase = e.action === 'settings' ? settingsPhrase(e.detail) : hasString(key) ? t(key) : e.action;
  const target = !withTarget || !e.targetEmail ? null
    : e.targetId ? <Link href={href(`/accounts/${e.targetId}`)}>{e.targetEmail}</Link> : <span>{e.targetEmail}</span>;
  return (
    <li>
      <span className="log-when muted small">{moment(e.at)}</span>
      <span>{withTarget ? <><strong>{e.by}</strong> {phrase} {target}</> : <>{phrase} <span className="muted small">{t('logBy', { by: e.by })}</span></>}</span>
    </li>
  );
}

/** Approve / decline / suspend / reactivate, as fits where the account is now. */
function StatusButtons({ u, busy, onSet }: { u: AdminUser; busy: boolean; onSet: (a: Approval) => void }) {
  if (u.approver) return null;
  const suspend = () => { if (confirm(t('confirmSuspend', { email: u.email }))) onSet('suspended'); };
  return (
    <span className="row">
      {(u.approval === 'pending' || u.approval === 'declined') && (
        <button className="primary" disabled={busy} onClick={() => onSet('approved')}>{t('approve')}</button>
      )}
      {u.approval === 'pending' && <button disabled={busy} onClick={() => onSet('declined')}>{t('decline')}</button>}
      {u.approval === 'approved' && <button disabled={busy} onClick={suspend}>{t('suspend')}</button>}
      {u.approval === 'suspended' && <button className="primary" disabled={busy} onClick={() => onSet('approved')}>{t('reactivate')}</button>}
    </span>
  );
}

/* ------------------------------------------------------------- overview */

/** The site controls (2026-10-19): approval, sign-ups, the activity emails, and the pause. */
function SiteControlsPanel() {
  const [s, setS] = useState<SiteControls | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.adminSettings().then((r) => { setS(r.settings); setMessage(r.settings.pausedMessage); }).catch((e) => setError((e as Error).message));
  }, []);
  const save = async (patch: Partial<SiteControls>) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r = await api.saveAdminSettings(patch);
      setS(r.settings);
      setNote(r.letIn ? t('ctlLetIn', { n: r.letIn }) : t('ctlSaved'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!s) return error ? <section className="admin-panel"><p className="error">{error}</p></section> : null;
  const toggle = (k: 'approvalRequired' | 'signUpsOpen' | 'notifySignUps' | 'notifySignIns', label: StringKey, hint?: StringKey) => (
    <label className="control-row">
      <input type="checkbox" checked={s[k]} disabled={busy} onChange={(e) => void save({ [k]: e.target.checked })} />
      <span><strong>{t(label)}</strong>{hint && <span className="muted small"> {t(hint)}</span>}</span>
    </label>
  );
  return (
    <section className={`admin-panel site-controls${s.paused ? ' danger-panel' : ''}`}>
      <h2>{t('controlsTitle')}</h2>
      {s.paused && <p className="error" role="alert"><strong>{t('ctlPausedNow')}</strong></p>}
      <p className="muted small">{t('controlsIntro')}</p>
      {toggle('approvalRequired', 'ctlApproval', 'ctlApprovalHint')}
      {toggle('signUpsOpen', 'ctlSignUps', 'ctlSignUpsHint')}
      {toggle('notifySignUps', 'ctlNotifySignUps')}
      {toggle('notifySignIns', 'ctlNotifySignIns', 'ctlNotifyHint')}
      <div className="pause-box">
        <label>{t('ctlPausedMessage')}
          <input value={message} maxLength={300} onChange={(e) => setMessage(e.target.value)} placeholder={t('pausedText')} />
        </label>
        {s.paused ? (
          <button className="primary" disabled={busy} onClick={() => void save({ paused: false, pausedMessage: message })}>{t('ctlResume')}</button>
        ) : (
          <button className="danger" disabled={busy} onClick={() => { if (confirm(t('ctlPauseConfirm'))) void save({ paused: true, pausedMessage: message }); }}>
            {t('ctlPause')}
          </button>
        )}
        <p className="muted small">{t('ctlPauseHint')}</p>
      </div>
      {note && <p className="ok small" role="status">{note}</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  );
}

function Overview({ href }: { href: Href }) {
  const [o, setO] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [log, setLog] = useState<AdminLogEntry[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => Promise.all([api.adminOverview(), api.adminUsers(), api.adminLog(8)])
    .then(([ov, u, l]) => { setO(ov); setUsers(u.users); setLog(l.entries); })
    .catch((e) => setError((e as Error).message));
  useEffect(() => { void load(); }, []);

  const decide = async (u: AdminUser, a: Approval) => {
    setBusy(u.id);
    setError(null);
    try {
      await api.setApproval(u.id, a);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!o) return error ? <p className="error">{error}</p> : <p className="muted">{t('loading')}</p>;
  const waiting = users.filter((u) => u.emailVerified && u.approval === 'pending');
  const a = o.accounts;
  return (
    <>
      <h1>{t('overview')}</h1>
      {error && <p className="error" role="alert">{error}</p>}
      <SiteControlsPanel />
      <div className="stats">
        <Stat label={t('statTotal')} value={a.total} href={href('/accounts')} />
        <Stat label={t('statWaiting')} value={a.waiting} href={`${href('/accounts')}?filter=waiting`} tone={a.waiting ? 'warn' : undefined} />
        <Stat label={t('statActive')} value={a.active} href={`${href('/accounts')}?filter=active`} />
        <Stat label={t('statSuspended')} value={a.suspended} href={`${href('/accounts')}?filter=suspended`} />
        <Stat label={t('statDeclined')} value={a.declined} href={`${href('/accounts')}?filter=declined`} />
        <Stat label={t('statUnconfirmed')} value={a.unconfirmed} href={`${href('/accounts')}?filter=unconfirmed`} />
        <Stat label={t('statSignUps')} value={o.signUps.last30} sub={t('statSignUpsSub', { week: o.signUps.last7 })} />
        <Stat label={t('statActiveWeek')} value={o.activeLast7} />
      </div>

      <section className="admin-panel">
        <h2>{t('waitingForYou')}</h2>
        {!waiting.length ? <p className="muted">{t('nobodyWaiting')}</p> : (
          <ul className="waiting-list">
            {waiting.map((u) => (
              <li key={u.id}>
                <Link href={href(`/accounts/${u.id}`)} className="who">
                  <strong>{u.name || u.email}</strong> <span className="muted small">{u.email}</span>
                  <span className="muted small"> · {t('signedUpOn', { when: ago(u.createdAt) })}</span>
                </Link>
                <StatusButtons u={u} busy={busy === u.id} onSet={(ap) => decide(u, ap)} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="admin-panel">
        <h2>{t('subscriptionsTitle')}</h2>
        <p className="muted">{t('subscriptionsOff')}</p>
      </section>

      <section className="admin-panel">
        <div className="row spread"><h2>{t('recentActivity')}</h2><Link href={href('/activity')}>{t('allActivity')}</Link></div>
        {!log.length ? <p className="muted">{t('noActivity')}</p> : <ul className="log-list">{log.map((e, i) => <LogLine key={i} e={e} href={href} />)}</ul>}
      </section>
    </>
  );
}

/* ------------------------------------------------------------- accounts */

type Filter = 'all' | 'waiting' | 'active' | 'suspended' | 'declined' | 'unconfirmed' | 'admins';
const FILTERS: [Filter, StringKey, (u: AdminUser) => boolean][] = [
  ['all', 'filterAll', () => true],
  ['waiting', 'statWaiting', (u) => u.emailVerified && u.approval === 'pending'],
  ['active', 'statActive', (u) => u.emailVerified && u.approval === 'approved'],
  ['suspended', 'statSuspended', (u) => u.approval === 'suspended'],
  ['declined', 'statDeclined', (u) => u.approval === 'declined'],
  ['unconfirmed', 'statUnconfirmed', (u) => !u.emailVerified],
  ['admins', 'filterAdmins', (u) => u.approver],
];

function Accounts({ href }: { href: Href }) {
  const loc = useLocation();
  const filter = (FILTERS.find(([f]) => f === loc.searchParams.get('filter'))?.[0] ?? 'all') as Filter;
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.adminUsers().then((r) => setUsers(r.users)).catch((e) => setError((e as Error).message)); }, []);

  const test = FILTERS.find(([f]) => f === filter)![2];
  const needle = q.trim().toLowerCase();
  const shown = (users || []).filter(test).filter((u) => !needle || `${u.name} ${u.email}`.toLowerCase().includes(needle));
  const count = (f: (u: AdminUser) => boolean) => (users || []).filter(f).length;

  return (
    <>
      <div className="row spread admin-title">
        <h1>{t('accounts')}</h1>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchAccounts')} aria-label={t('searchAccounts')} />
      </div>
      <nav className="tabs" aria-label={t('accounts')}>
        {FILTERS.map(([f, label, fn]) => (
          <button key={f} aria-pressed={filter === f}
            onClick={() => navigate(f === 'all' ? href('/accounts') : `${href('/accounts')}?filter=${f}`, { replace: true })}>
            {t(label)} ({count(fn)})
          </button>
        ))}
      </nav>
      {error && <p className="error" role="alert">{error}</p>}
      {!users ? <p className="muted">{t('loading')}</p> : !shown.length ? <p className="muted">{t('nobodyHere')}</p> : (
        <div className="acct-table" role="table" aria-label={t('accounts')}>
          <div className="acct-row acct-head" role="row">
            {(['colAccount', 'colStatus', 'colJoined', 'colLastActive'] as StringKey[])
              .map((k) => <span key={k} role="columnheader">{t(k)}</span>)}
          </div>
          {shown.map((u) => (
            <Link key={u.id} href={href(`/accounts/${u.id}`)} className="acct-row" role="row">
              <span className="acct-who" role="cell"><strong>{u.name || u.email}</strong><span className="muted small">{u.email}</span></span>
              <span role="cell"><StatusChip u={u} /></span>
              <span role="cell" data-label={t('colJoined')}>{day(u.createdAt)}</span>
              <span role="cell" data-label={t('colLastActive')}>{u.lastSeenAt ? ago(u.lastSeenAt) : t('never')}</span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

/* ---------------------------------------------------------- one account */

function AccountDetail({ id, href }: { id: string; href: Href }) {
  const [d, setD] = useState<AdminUserDetail | null>(null);
  const [gone, setGone] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const [busy, setBusy] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  const load = () => api.adminUser(id).then(setD).catch((e) => {
    setMsg({ err: e instanceof ApiError && e.status === 404 ? t('nobodyHere') : (e as Error).message });
  });
  useEffect(() => { void load(); }, [id]);

  /** Do something, show what came of it, and look at the account again. */
  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    setMsg({});
    try {
      const ok = await fn();
      await load();
      if (ok) setMsg({ ok });
    } catch (e) {
      setMsg({ err: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg({});
    try {
      await api.adminDeleteUser(id, confirmEmail);
      setGone(d!.user.email);
    } catch (err) {
      setMsg({ err: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  if (gone) {
    return (
      <>
        <p><Link href={href('/accounts')}>{t('backToAccounts')}</Link></p>
        <p className="ok">{t('accountDeleted', { email: gone })}</p>
      </>
    );
  }
  if (!d) return <><p><Link href={href('/accounts')}>{t('backToAccounts')}</Link></p>{msg.err ? <p className="error">{msg.err}</p> : <p className="muted">{t('loading')}</p>}</>;
  const u = d.user;

  return (
    <div className="acct-detail">
      <p><Link href={href('/accounts')}>{t('backToAccounts')}</Link></p>
      <div className="acct-title">
        <div>
          <h1>{u.name || u.email}</h1>
          <p className="muted">{u.email}</p>
        </div>
        <StatusChip u={u} />
      </div>
      {msg.ok && <p className="ok" role="status">{msg.ok}</p>}
      {msg.err && <p className="error" role="alert">{msg.err}</p>}

      <div className="detail-grid">
        <section className="admin-panel">
          <dl className="facts">
            <dt>{t('factJoined')}</dt><dd>{moment(u.createdAt)}</dd>
            <dt>{t('factLastSignIn')}</dt><dd>{u.lastSignInAt ? ago(u.lastSignInAt) : t('never')}</dd>
            <dt>{t('factLastActive')}</dt><dd>{u.lastSeenAt ? ago(u.lastSeenAt) : t('never')}</dd>
            {u.decidedAt && <><dt>{t('factDecided')}</dt><dd>{moment(u.decidedAt)}</dd></>}
            <dt>{t('factTerms')}</dt>
            <dd>{!u.termsVersion ? t('factTermsNot') : u.termsCurrent ? `${u.termsVersion} · ${day(u.termsAcceptedAt)}`
              : t('factTermsOld', { version: u.termsVersion })}</dd>
            <dt>{t('factPlan')}</dt><dd>{t('planFree')}</dd>
            {u.promoCode && <><dt>{t('factPromo')}</dt><dd>{u.promoCode}{u.approval === 'pending' ? ` · ${t('promoNotUsed')}` : ''}</dd></>}
            <dt>{t('factTheme')}</dt><dd>{u.theme}</dd>
          </dl>
        </section>
      </div>

      <section className="admin-panel">
        <h2>{t('actions')}</h2>
        {u.approver && <p className="muted small">{t('adminManagedHint')}</p>}
        <div className="row">
          <StatusButtons u={u} busy={busy} onSet={(a) => run(async () => { await api.setApproval(u.id, a); })} />
          {!u.emailVerified && (
            <button disabled={busy} onClick={() => run(async () => { await api.adminResendConfirmation(u.id); return t('confirmationSent', { email: u.email }); })}>
              {t('resendConfirmation')}
            </button>
          )}
          <button disabled={busy} onClick={() => run(async () => { await api.adminPasswordReset(u.id); return t('passwordResetSent', { email: u.email }); })}>
            {t('sendPasswordReset')}
          </button>
        </div>
      </section>

      <section className="admin-panel">
        <div className="row spread">
          <h2>{t('devices')}</h2>
          {!!d.sessions.length && !u.approver && (
            <button disabled={busy} onClick={() => run(async () => t('signedOutN', { n: (await api.adminSignOut(u.id)).signedOut }))}>
              {t('signOutEverywhere')}
            </button>
          )}
        </div>
        {!d.sessions.length ? <p className="muted">{t('noDevices')}</p> : (
          <ul className="device-list">
            {d.sessions.map((s) => (
              <li key={s.id}>
                <strong>{deviceName(s.userAgent)}</strong>
                <span className="muted small">{s.ip || '—'} · {t('signedInSince', { when: ago(s.createdAt) })} · {t('lastUsed', { when: ago(s.lastUsedAt) })}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="admin-panel">
        <h2>{t('history')}</h2>
        {!d.log.length ? <p className="muted">{t('noActivity')}</p> : <ul className="log-list">{d.log.map((e, i) => <LogLine key={i} e={e} href={href} withTarget={false} />)}</ul>}
      </section>

      {!u.approver && (
        <section className="admin-panel danger-panel">
          <h2>{t('deleteThisAccount')}</h2>
          <p className="muted">{t('deleteThisAccountBody')}</p>
          <form onSubmit={remove} className="row">
            <input type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={u.email} aria-label={t('email')} />
            <button className="danger" disabled={busy || confirmEmail.trim().toLowerCase() !== u.email.toLowerCase()}>{t('deleteThisAccount')}</button>
          </form>
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- activity */

function Activity({ href }: { href: Href }) {
  const [entries, setEntries] = useState<AdminLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.adminLog(300).then((r) => setEntries(r.entries)).catch((e) => setError((e as Error).message)); }, []);
  return (
    <>
      <h1>{t('activity')}</h1>
      {error && <p className="error" role="alert">{error}</p>}
      {!entries ? <p className="muted">{t('loading')}</p> : !entries.length ? <p className="muted">{t('noActivity')}</p> : (
        <section className="admin-panel"><ul className="log-list">{entries.map((e, i) => <LogLine key={i} e={e} href={href} />)}</ul></section>
      )}
    </>
  );
}

/* -------------------------------------------------------------- backups */

/**
 * Promo codes (apps/api/src/promo.ts, 2026-10-18): each lets new accounts in without waiting
 * for approval - typed at sign-up, on the waiting page, or carried by a sign-up link.
 */
function PromoPage({ appUrl }: { appUrl: string }) {
  const [codes, setCodes] = useState<PromoCode[] | null>(null);
  const empty = { code: '', note: '', maxUses: '', expiresAt: '' };
  const [form, setForm] = useState(empty);
  const [state, setState] = useState<{ busy?: boolean; msg?: string; err?: string }>({});
  const load = () => api.adminPromoCodes().then((r) => setCodes(r.codes));
  useEffect(() => { load().catch((e) => setState({ err: (e as Error).message })); }, []);
  const run = async (fn: () => Promise<string | void>) => {
    setState({ busy: true });
    try {
      const msg = await fn();
      await load();
      setState({ msg: msg || undefined });
    } catch (e) {
      setState({ err: (e as Error).message });
    }
  };
  const make = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const c = await api.adminCreatePromo({ code: form.code.trim() || undefined, note: form.note.trim() || undefined,
        maxUses: form.maxUses ? Number(form.maxUses) : undefined, expiresAt: form.expiresAt || undefined });
      setForm(empty);
      return t('promoMade', { code: c.code });
    });
  };
  const link = (code: string) => `${appUrl}/signup?promo=${encodeURIComponent(code)}`;
  const copy = (text: string) => run(async () => { await navigator.clipboard.writeText(text); return t('promoCopied'); });
  const status = (c: PromoCode) => !c.active ? t('promoOff')
    : c.expiresAt && new Date(c.expiresAt).getTime() < Date.now() ? t('promoExpired')
    : c.maxUses !== null && c.uses >= c.maxUses ? t('promoUsedUp') : t('promoWorking');
  const set = (k: keyof typeof empty) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <div className="row spread admin-title"><h1>{t('navPromo')}</h1></div>
      <p className="muted">{t('promoIntro')}</p>
      {state.msg && <p className="ok" role="status">{state.msg}</p>}
      {state.err && <p className="error" role="alert">{state.err}</p>}
      <section className="admin-panel">
        <h2>{t('promoNew')}</h2>
        <form className="promo-new" onSubmit={make}>
          <label>{t('promoCodeLabel')}<input value={form.code} onChange={set('code')} placeholder={t('promoCodeAuto')} autoCapitalize="characters" spellCheck={false} /></label>
          <label>{t('promoNote')}<input value={form.note} onChange={set('note')} placeholder={t('promoNoteHint')} maxLength={200} /></label>
          <label>{t('promoMaxUses')}<input type="number" min={1} value={form.maxUses} onChange={set('maxUses')} placeholder={t('promoNoLimit')} /></label>
          <label>{t('promoLastDay')}<input type="date" value={form.expiresAt} onChange={set('expiresAt')} /></label>
          <button className="primary" disabled={state.busy}>{t('promoMake')}</button>
        </form>
      </section>
      <section className="admin-panel">
        {!codes ? <p className="muted">{t('loading')}</p> : !codes.length ? <p className="muted small">{t('promoNone')}</p> : (
          <div className="acct-table" role="table" aria-label={t('navPromo')}>
            <div className="acct-row promo-row acct-head" role="row">
              {(['promoCodeLabel', 'promoUsed', 'promoLastDay', 'colStatus', 'actions'] as StringKey[]).map((k) => <span key={k} role="columnheader">{t(k)}</span>)}
            </div>
            {codes.map((c) => (
              <div key={c.code} className="acct-row promo-row" role="row">
                <span className="acct-who" role="cell"><strong className="promo-code">{c.code}</strong>{c.note && <span className="muted small">{c.note}</span>}</span>
                <span role="cell" data-label={t('promoUsed')}>{c.maxUses === null ? c.uses : `${c.uses} / ${c.maxUses}`}</span>
                <span role="cell" data-label={t('promoLastDay')}>{c.expiresAt ? day(c.expiresAt) : t('promoNoEnd')}</span>
                <span role="cell"><span className={`status ${c.usable ? 'status-approved' : 'status-declined'}`}>{status(c)}</span></span>
                <span role="cell" className="row promo-actions">
                  <button className="small" disabled={state.busy} onClick={() => void copy(c.code)}>{t('promoCopyCode')}</button>
                  <button className="small" disabled={state.busy} onClick={() => void copy(link(c.code))}>{t('promoCopyLink')}</button>
                  <button className="small" disabled={state.busy} onClick={() => void run(async () => { await api.adminSetPromoActive(c.code, !c.active); })}>
                    {c.active ? t('promoSwitchOff') : t('promoSwitchOn')}
                  </button>
                  {c.uses === 0 && (
                    <button className="small" disabled={state.busy}
                      onClick={() => { if (confirm(t('promoDeleteConfirm', { code: c.code }))) void run(async () => { await api.adminDeletePromo(c.code); }); }}>
                      {t('promoDelete')}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/** The daily database backups: the newest first, each one a download. */
/**
 * The picture library's photographs (apps/api/src/library.ts): add one - made
 * smaller here first, with the same code as people's own pictures - or remove one.
 */
function LibraryPage() {
  const [items, setItems] = useState<LibraryPhoto[] | null>(null);
  /** One picture or many: with many, each is named after its file and all go into the group. */
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');
  const [state, setState] = useState<{ busy?: boolean; msg?: string; err?: string }>({});
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => { api.adminLibrary().then((r) => setItems(r.items)).catch((e) => { setItems([]); setState({ err: (e as Error).message }); }); }, []);
  const groups = [...new Set([...OCCASION_GROUPS, ...(items || []).map((i) => i.group)])];

  /** "03-misty-forest-in-oregon.jpg" -> "Misty forest in oregon" */
  const nameOf = (file: File) => {
    const n = file.name.replace(/\.[^.]+$/, '').replace(/^\d+[-_ ]*/, '').replace(/[-_]+/g, ' ').trim();
    return (n.charAt(0).toUpperCase() + n.slice(1)).slice(0, 60) || 'Picture';
  };
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!files.length) return;
    const failed: string[] = [];
    let added = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setState({ busy: true, msg: files.length > 1 ? t('libraryAddingN', { n: i + 1, of: files.length }) : undefined });
      try {
        const p = await preparePicture(file);
        const f = new FormData();
        f.set('name', files.length === 1 ? name.trim() : nameOf(file));
        f.set('group', group.trim());
        // the two sizes, made here; the picture's size is the preview's
        for (const x of p.files) {
          f.set(x.kind, new File([x.blob], `${x.kind}.jpg`, { type: 'image/jpeg' }));
          if (x.kind === 'preview') { f.set('width', String(x.width)); f.set('height', String(x.height)); }
        }
        const made = await api.adminAddLibrary(f);
        setItems((l) => [...(l || []), made]);
        added++;
      } catch (err) {
        failed.push(`${file.name}: ${(err as Error).message}`);
      }
    }
    setState({ msg: added ? t('libraryAddedN', { n: added }) : undefined, err: failed.length ? failed.join(' · ') : undefined });
    setFiles([]); setName('');
    if (fileInput.current) fileInput.current.value = '';
  };
  const remove = async (item: LibraryPhoto) => {
    if (!confirm(t('libraryConfirmRemove', { name: item.name }))) return;
    try {
      await api.adminDeleteLibrary(item.id);
      setItems((l) => (l || []).filter((x) => x.id !== item.id));
    } catch (err) {
      setState({ err: (err as Error).message });
    }
  };

  return (
    <>
      <div className="row spread admin-title"><h1>{t('libraryTitle')}</h1></div>
      <p className="muted">{t('libraryIntro')}</p>
      <section className="admin-panel">
        <h2>{t('libraryAdd')}</h2>
        <form className="stack library-form" onSubmit={add}>
          <label>{t('libraryFile')}
            <input ref={fileInput} type="file" accept="image/*" multiple required onChange={(e) => {
              const list = [...(e.target.files ?? [])];
              setFiles(list);
              if (list.length === 1 && !name) setName(nameOf(list[0]));
            }} />
            <span className="muted small">{t('libraryFilesHint')}</span>
          </label>
          {files.length <= 1 ? (
            <label>{t('libraryName')}<input value={name} maxLength={60} required onChange={(e) => setName(e.target.value)} /></label>
          ) : <p className="small">{t('libraryManyChosen', { n: files.length })}</p>}
          <label>{t('libraryGroup')}
            <input value={group} maxLength={40} required list="library-groups" onChange={(e) => setGroup(e.target.value)} />
            <datalist id="library-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
            <span className="muted small">{t('libraryGroupHint')}</span>
          </label>
          {state.err && <p className="error" role="alert">{state.err}</p>}
          {state.msg && <p className="ok" role="status">{state.msg}</p>}
          <button className="primary" disabled={state.busy || !files.length || (files.length === 1 && !name.trim()) || !group.trim()}>
            {state.busy ? t('libraryAdding') : t('libraryUpload')}
          </button>
        </form>
      </section>
      <section className="admin-panel">
        {!items ? <p className="muted">{t('loading')}</p> : !items.length ? <p className="muted small">{t('libraryEmpty')}</p> : (
          groups.filter((g) => items.some((i) => i.group === g)).map((g) => (
            <div key={g}>
              <h3>{g}</h3>
              <ul className="library-admin">
                {items.filter((i) => i.group === g).map((i) => (
                  <li key={i.id}>
                    <img src={i.urls.thumb} alt={i.name} loading="lazy" />
                    <span className="small">{i.name}</span>
                    <span className="muted small">{Math.round(i.bytes / 1024)} KB</span>
                    <button onClick={() => void remove(i)}>{t('libraryRemove')}</button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
    </>
  );
}

function BackupsPage() {
  const [names, setNames] = useState<string[] | null>(null);
  const [state, setState] = useState<{ busy?: boolean; msg?: string; err?: string }>({});
  useEffect(() => { api.backups().then((r) => setNames(r.backups)).catch(() => setNames([])); }, []);
  const makeNow = async () => {
    setState({ busy: true });
    try {
      const b = await api.makeBackup();
      setNames((l) => [b.name, ...(l || [])]);
      setState({ msg: t('backupMade', { kb: Math.max(1, Math.round(b.bytes / 1024)) }) });
    } catch (e) {
      setState({ err: (e as Error).message });
    }
  };
  const when = (name: string) => new Date(name.replace(/T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z.*$/, 'T$1:$2:$3.$4Z')).toLocaleString();
  return (
    <>
      <div className="row spread admin-title">
        <h1>{t('backups')}</h1>
        <button onClick={makeNow} disabled={state.busy}>{t('backupNow')}</button>
      </div>
      <p className="muted">{t('backupsIntro')}</p>
      {state.msg && <p className="ok">{state.msg}</p>}
      {state.err && <p className="error">{state.err}</p>}
      <section className="admin-panel">
        {!names ? <p className="muted">{t('loading')}</p> : !names.length ? <p className="muted small">{t('noBackupsYet')}</p> : (
          <ul className="backup-list">
            {names.map((n) => (
              <li key={n}><span>{when(n)}</span> <a href={api.backupUrl(n)} download>{t('download')}</a></li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
